/* ============================================================
   Custom Header — interaction layer
   - Hover + click open on desktop
   - Keyboard / Esc / outside click to close
   - Mobile drawer with stacked submenus
   ============================================================ */

(function () {
  const DESKTOP_MQ = '(min-width: 990px)';
  const HOVER_CLOSE_DELAY = 140; // ms — small grace period between trigger and panel
  const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches;

  class CustomHeader extends HTMLElement {
    constructor() {
      super();
      this.activeTrigger = null;
      this.activePanel = null;
      this.closeTimer = null;

      this.onDocClick = this.onDocClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-mega-panel]'));
      this.megaWrap = this.querySelector('[data-mega-wrap]');
      this.mobileToggle = this.querySelector('[data-mobile-toggle]');
      this.mobileDrawer = this.querySelector('[data-mobile-drawer]');
      this.mobileCloseButtons = Array.from(this.querySelectorAll('[data-mobile-close]'));
      this.mobileSubOpenButtons = Array.from(this.querySelectorAll('[data-mobile-sub-open]'));
      this.mobileSubBackButtons = Array.from(this.querySelectorAll('[data-mobile-sub-back]'));
      this.mobileSubs = Array.from(this.querySelectorAll('[data-mobile-sub]'));
      this.overlay = this.querySelector('[data-overlay]');
      this.localeForms = Array.from(this.querySelectorAll('[data-locale-form]'));

      this.bindDesktop();
      this.bindMobile();
      this.bindLocale();

      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeyDown);
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('resize', this.onResize);
    }

    /* ---------- Desktop ---------- */
    bindDesktop() {
      this.triggers.forEach((trigger) => {
        const id = trigger.getAttribute('data-mega-trigger');
        const panel = this.querySelector(`[data-mega-panel="${id}"]`);
        if (!panel) return;

        const item = trigger.closest('.custom-header__nav-item');

        // Click toggle (also primary path for keyboard)
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.activePanel === panel) {
            this.closeAll();
          } else {
            this.open(trigger, panel);
          }
        });

        // Hover open (desktop only)
        if (item) {
          item.addEventListener('mouseenter', () => {
            if (!isDesktop()) return;
            this.cancelClose();
            this.open(trigger, panel);
          });
          item.addEventListener('mouseleave', () => {
            if (!isDesktop()) return;
            this.scheduleClose();
          });
        }

        // Keep open while hovering the panel
        panel.addEventListener('mouseenter', () => {
          if (!isDesktop()) return;
          this.cancelClose();
        });
        panel.addEventListener('mouseleave', () => {
          if (!isDesktop()) return;
          this.scheduleClose();
        });
      });
    }

    open(trigger, panel) {
      if (this.activeTrigger && this.activeTrigger !== trigger) {
        this.activeTrigger.setAttribute('aria-expanded', 'false');
      }
      if (this.activePanel && this.activePanel !== panel) {
        this.activePanel.classList.remove('is-active');
        this.activePanel.setAttribute('aria-hidden', 'true');
      }

      trigger.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-active');
      panel.setAttribute('aria-hidden', 'false');

      this.activeTrigger = trigger;
      this.activePanel = panel;
      this.classList.add('is-mega-open');
    }

    closeAll() {
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((p) => {
        p.classList.remove('is-active');
        p.setAttribute('aria-hidden', 'true');
      });
      this.activeTrigger = null;
      this.activePanel = null;
      this.classList.remove('is-mega-open');
    }

    scheduleClose() {
      this.cancelClose();
      this.closeTimer = window.setTimeout(() => this.closeAll(), HOVER_CLOSE_DELAY);
    }

    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    /* ---------- Mobile drawer ---------- */
    bindMobile() {
      if (this.mobileToggle && this.mobileDrawer) {
        this.mobileToggle.addEventListener('click', () => this.openMobile());
      }

      this.mobileCloseButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.closeMobile());
      });

      this.mobileSubOpenButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-mobile-sub-open');
          this.openMobileSub(id);
        });
      });

      this.mobileSubBackButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = btn.closest('[data-mobile-sub]');
          if (sub) this.closeMobileSub(sub);
        });
      });
    }

    openMobile() {
      if (!this.mobileDrawer) return;
      this.mobileDrawer.classList.add('is-active');
      this.mobileDrawer.setAttribute('aria-hidden', 'false');
      this.mobileToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('custom-header-locked');
      window.requestAnimationFrame(() => {
        const firstClose = this.mobileCloseButtons[0];
        firstClose?.focus();
      });
    }

    closeMobile() {
      if (!this.mobileDrawer) return;
      this.mobileSubs.forEach((sub) => this.closeMobileSub(sub));
      this.mobileDrawer.classList.remove('is-active');
      this.mobileDrawer.setAttribute('aria-hidden', 'true');
      this.mobileToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('custom-header-locked');
      this.mobileToggle?.focus();
    }

    openMobileSub(id) {
      const sub = this.querySelector(`#${CSS.escape(id)}`);
      if (!sub) return;
      sub.classList.add('is-active');
      sub.setAttribute('aria-hidden', 'false');
    }

    closeMobileSub(sub) {
      sub.classList.remove('is-active');
      sub.setAttribute('aria-hidden', 'true');
    }

    /* ---------- Locale form (Shopify language picker) ---------- */
    bindLocale() {
      this.localeForms.forEach((form) => {
        const input = form.querySelector('input[name="locale_code"], input[name="country_code"]');
        const toggle = form.querySelector('[data-locale-toggle]');
        const list = form.querySelector('[data-locale-list]');
        const root = form.closest('[data-locale-root]');

        if (toggle && list) {
          toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = toggle.getAttribute('aria-expanded') === 'true';
            toggle.setAttribute('aria-expanded', String(!open));
            list.setAttribute('aria-hidden', String(open));
            if (root) root.classList.toggle('is-open', !open);
          });
        }

        form.querySelectorAll('[data-value]').forEach((item) => {
          item.addEventListener('click', (e) => {
            e.preventDefault();
            if (!input) return;
            input.value = item.getAttribute('data-value');
            form.submit();
          });
        });
      });

      // Close desktop popover on outside click
      document.addEventListener('click', (e) => {
        this.querySelectorAll('[data-locale-root].is-open').forEach((root) => {
          if (!root.contains(e.target)) {
            root.classList.remove('is-open');
            const tgl = root.querySelector('[data-locale-toggle]');
            const lst = root.querySelector('[data-locale-list]');
            if (tgl) tgl.setAttribute('aria-expanded', 'false');
            if (lst) lst.setAttribute('aria-hidden', 'true');
          }
        });
      });
    }

    isMobileOpen() {
      return this.mobileDrawer?.classList.contains('is-active');
    }

    /* ---------- Document-level handlers ---------- */
    onDocClick(e) {
      if (!this.contains(e.target)) {
        this.closeAll();
      }
    }

    onKeyDown(e) {
      if (e.key !== 'Escape') return;
      if (this.activePanel) {
        this.closeAll();
        this.activeTrigger?.focus();
      } else if (this.isMobileOpen()) {
        const openSub = this.mobileSubs.find((s) => s.classList.contains('is-active'));
        if (openSub) {
          this.closeMobileSub(openSub);
        } else {
          this.closeMobile();
        }
      }
    }

    onResize() {
      if (isDesktop() && this.isMobileOpen()) {
        this.closeMobile();
      }
      if (!isDesktop() && this.activePanel) {
        this.closeAll();
      }
    }
  }

  if (!customElements.get('custom-header')) {
    customElements.define('custom-header', CustomHeader);
  }
})();
